import api from './api';

// 청크는 256KB의 배수여야 한다 (마지막 청크만 예외) — Google 재개 가능 업로드 규격
const UNIT = 256 * 1024;
const SMALL_CHUNK = 16 * 1024 * 1024;   // 1GB 미만
const LARGE_CHUNK = 128 * 1024 * 1024;  // 1GB 이상: 요청 수를 줄인다
const MAX_RETRY = 6;

// 중단된 업로드를 새로고침 후에도 이어받기 위해 세션을 남겨둔다.
// 브라우저는 File 핸들을 저장할 수 없으므로, 같은 파일을 다시 고를 때만 이어받을 수 있다.
// 여러 파일을 동시에 올리므로 파일별로 따로 보관한다.
const STORE_KEY = 'drive_upload_sessions';
const SESSION_TTL = 6 * 24 * 60 * 60 * 1000; // 세션 URI는 1주일 뒤 만료된다

function readStore() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}'); } catch { return {}; }
}

function writeStore(obj) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(obj)); } catch { /* 무시 */ }
}

function chunkSizeFor(size) {
  return size >= 1024 ** 3 ? LARGE_CHUNK : SMALL_CHUNK;
}

function fileKey(file, folderId) {
  return [file.name, file.size, file.lastModified, folderId || 'root'].join('|');
}

function saveSession(file, folderId, sessionUrl) {
  const store = readStore();
  store[fileKey(file, folderId)] = { sessionUrl, at: Date.now() };
  writeStore(store);
}

function loadSession(file, folderId) {
  const store = readStore();
  const entry = store[fileKey(file, folderId)];
  if (!entry) return null;
  if (Date.now() - entry.at > SESSION_TTL) return null;
  return entry.sessionUrl;
}

export function clearSession(file, folderId) {
  // 인자가 없으면 만료된 것만 정리한다 (다른 파일의 진행 중인 세션을 지우지 않기 위해)
  const store = readStore();
  if (file) delete store[fileKey(file, folderId)];
  else for (const [k, v] of Object.entries(store)) {
    if (Date.now() - (v?.at || 0) > SESSION_TTL) delete store[k];
  }
  writeStore(store);
}

// 이미 서버가 받은 바이트 수를 물어본다. 0부터 다시 올리지 않기 위한 확인.
async function fetchOffset(sessionUrl, size) {
  const r = await fetch(sessionUrl, {
    method: 'PUT',
    headers: { 'Content-Range': `bytes */${size}` },
  });
  if (r.status === 200 || r.status === 201) return { done: true, file: await r.json() };
  if (r.status === 308) {
    const range = r.headers.get('range');           // 예: "bytes=0-262143"
    const received = range ? Number(range.split('-')[1]) + 1 : 0;
    return { done: false, offset: received };
  }
  // 404/410이면 세션이 만료·폐기된 것이다
  return { done: false, offset: null };
}

const sleep = ms => new Promise(r => setTimeout(r, ms));

/**
 * Drive에 파일을 올린다. 브라우저가 구글로 직접 전송하므로
 * 우리 서버의 본문 크기 제한(100MB)을 받지 않는다.
 *
 * @param {File}     file
 * @param {string}   folderId
 * @param {object}   opts
 * @param {Function} opts.onProgress  ({ loaded, total, ratio }) => void
 * @param {AbortSignal} opts.signal
 * @returns {Promise<object>} 생성된 Drive 파일
 */
export async function uploadToDrive(file, folderId, { onProgress, signal } = {}) {
  const total = file.size;

  // 중단됐던 같은 파일이면 기존 세션을 이어쓴다
  let sessionUrl = loadSession(file, folderId);
  let offset = 0;

  if (sessionUrl) {
    const probe = await fetchOffset(sessionUrl, total).catch(() => ({ offset: null }));
    if (probe.done) { clearSession(file, folderId); return probe.file; }
    if (probe.offset === null) { clearSession(file, folderId); sessionUrl = null; }
    else offset = probe.offset;
  }

  if (!sessionUrl) {
    const { data } = await api.post('/google/drive/upload-session', {
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: total,
      folderId,
    });
    sessionUrl = data.sessionUrl;
    offset = 0;
    saveSession(file, folderId, sessionUrl);
  }

  onProgress?.({ loaded: offset, total, ratio: total ? offset / total : 0 });

  const chunkSize = Math.max(UNIT, Math.floor(chunkSizeFor(total) / UNIT) * UNIT);
  let attempt = 0;

  while (offset < total) {
    if (signal?.aborted) throw new DOMException('업로드를 취소했습니다.', 'AbortError');

    const end = Math.min(offset + chunkSize, total);
    // Blob 조각을 그대로 body로 넘긴다 — 파일 전체를 메모리에 올리지 않는다
    const blob = file.slice(offset, end);

    let res;
    try {
      res = await fetch(sessionUrl, {
        method: 'PUT',
        headers: { 'Content-Range': `bytes ${offset}-${end - 1}/${total}` },
        body: blob,
        signal,
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      // 네트워크가 끊겼다 — 실제로 어디까지 갔는지 확인하고 이어서 보낸다
      if (++attempt > MAX_RETRY) throw new Error('네트워크 오류로 업로드를 이어가지 못했습니다.');
      await sleep(Math.min(30000, 1000 * 2 ** attempt));
      const probe = await fetchOffset(sessionUrl, total).catch(() => ({ offset: null }));
      if (probe.done) { clearSession(file, folderId); return probe.file; }
      if (probe.offset === null) { clearSession(file, folderId); throw new Error('업로드 세션이 만료되어 처음부터 다시 올려야 합니다.'); }
      offset = probe.offset;
      continue;
    }

    if (res.status === 200 || res.status === 201) {
      clearSession(file, folderId);
      const done = await res.json();
      onProgress?.({ loaded: total, total, ratio: 1 });
      return done;
    }

    if (res.status === 308) {
      const range = res.headers.get('range');
      offset = range ? Number(range.split('-')[1]) + 1 : end;
      attempt = 0;
      onProgress?.({ loaded: offset, total, ratio: total ? offset / total : 0 });
      continue;
    }

    // 5xx / 429는 재시도 대상, 나머지는 실패로 확정
    if (res.status >= 500 || res.status === 429) {
      if (++attempt > MAX_RETRY) throw new Error(`업로드 실패 (HTTP ${res.status})`);
      await sleep(Math.min(30000, 1000 * 2 ** attempt));
      const probe = await fetchOffset(sessionUrl, total).catch(() => ({ offset: null }));
      if (probe.done) { clearSession(file, folderId); return probe.file; }
      if (probe.offset !== null) offset = probe.offset;
      continue;
    }

    clearSession(file, folderId);
    const detail = await res.text().catch(() => '');
    throw new Error(`업로드 실패 (HTTP ${res.status}) ${detail.slice(0, 120)}`);
  }

  // 루프가 끝났는데 완료 응답을 못 받은 경우
  const probe = await fetchOffset(sessionUrl, total);
  clearSession(file, folderId);
  if (probe.done) return probe.file;
  throw new Error('업로드가 끝나지 않았습니다.');
}
